import React from 'react'
import { node } from 'prop-types'
import { useSelector, useDispatch } from 'react-redux'
import { createScrollStopListener, getEntryPoint } from 'settings/app/helpers'
import { __dev__, GOOGLE_DISTANCE_CALC } from 'settings/app/constants'
import { getLocationDistanceFromResults } from 'redux/actions/searchActions'
import { useMapPrivacyPolicy } from 'hooks'

/**
 *  Scroll handle for distance request
 * @param children
 * @param resultListRef
 * @returns {JSX.Element}
 * @constructor
 */
const DistanceCalculation = ({ children }) => {
  const entryPoint = getEntryPoint()
  const dispatch = useDispatch()
  const { search } = useSelector((state) => state)
  const { clientHasSearched, results, searchLocation } = search
  const [requestIDs, setRequestIDs] = React.useState([])
  const { wantGoogleMaps } = useMapPrivacyPolicy()
  const isHOCEnabled =
    wantGoogleMaps &&
    !!searchLocation.lat &&
    !!searchLocation.lng &&
    GOOGLE_DISTANCE_CALC.includes(entryPoint) &&
    !__dev__ // save money for our customer

  /**
   * check if a element is fully in viewport
   * @param elem
   * @returns {boolean}
   */
  const isInViewport = (elem) => {
    const bounding = elem.getBoundingClientRect()
    return (
      bounding.top >= 0 &&
      bounding.left >= 0 &&
      bounding.bottom <=
        (window.innerHeight || document.documentElement.clientHeight) &&
      bounding.right <=
        (window.innerWidth || document.documentElement.clientWidth)
    )
  }

  /**
   * execute viewport check when results are there
   * @type {(function(): void)|*}
   */
  const checkVisibleResults = React.useCallback(() => {
    if (Object.keys(results).length > 0) {
      const resultlist = document.querySelector('#resultlist')
      const visibleIds = []

      if (resultlist?.children) {
        let activeElements = 0
        const currentResults = Array.from(resultlist.children)

        // parse all html element if they are rendered
        currentResults.forEach((result) => {
          const currentResultId = result.getAttribute('data-id')
          const persisResId = Object.keys(results).find(
            (item, index) => results[index].id === currentResultId
          )

          // skip calculated elements
          if (
            result.getAttribute('xhr-done') &&
            !!results[persisResId].locationDistance
          ) {
            return true
          }

          // tile headline is rendered
          if (isInViewport(result.querySelector('.headline'))) {
            activeElements += 1
            visibleIds.push(currentResultId)
            result.setAttribute('xhr-done', 'true')

            // if (__dev__) {
            //     result.style.border = '4px solid orange'
            // }
          }
        })

        if (!!activeElements) setRequestIDs(visibleIds)
      }
    }
  }, [results])

  /**
   * appending scroll stop detection
   */
  React.useEffect(() => {
    if (!!entryPoint && isHOCEnabled) {
      const destroyListener = createScrollStopListener(window, () => {
        // setTimeout(checkVisibleResults, 500)
        checkVisibleResults()
      })
      return () => destroyListener() // when App component is unmounted
    }
  })

  /**
   * initial check user has searched
   */
  React.useEffect(() => {
    if (clientHasSearched && isHOCEnabled) {
      checkVisibleResults()
    }
  }, [checkVisibleResults, clientHasSearched, entryPoint, isHOCEnabled])

  /**
   * dispatch action (perform xhr)
   * and reset requestIDs
   */
  React.useEffect(() => {
    if (requestIDs.length > 0) {
      dispatch(getLocationDistanceFromResults(requestIDs)).then(() => {
        setRequestIDs([])
      })
    }
  }, [dispatch, requestIDs])

  return <>{children}</>
}

DistanceCalculation.propTypes = {
  children: node.isRequired,
}

export default React.memo(DistanceCalculation)
// export default DistanceCalculation
